/**
 * Compliance coverage — reading GET /safety/compliance, and the one arithmetic
 * question it asks.
 *
 * ── THREE STATES, NOT TWO ────────────────────────────────────────────────────
 * Same shape as the rule in `safety-metrics.ts`, one endpoint over:
 *
 *   payload absent / unusable      → we do not know. An ERROR, not a bar.
 *   total 0, completed 0           → nothing to measure. An em dash.
 *   total > 0                      → a real percentage. A RESULT.
 *
 * All three used to render as `0/0 · 0%` in red. A tenant with no jobs in the
 * 30-day window, a tenant whose compliance call 500'd, and a tenant genuinely
 * failing every JSA were indistinguishable on screen — and two of the three
 * were being told they had a compliance problem they did not have.
 *
 * ── WHY 0% IS THE WRONG ANSWER TO 0/0 ────────────────────────────────────────
 * The numerator is honestly zero; the PERCENTAGE is undefined. `0` for an
 * undefined ratio is the `parseFloat(x) || 0` bug wearing a division sign: it
 * renders a plausible figure where there is no figure, and the colour ladder
 * then paints it red because 0 < 60. API PR #360 made the empty denominator
 * reachable — a company in its first week, or a quiet month, now reads as
 * failing. `pct` returns null here for exactly that reason and there must
 * never be a `|| 0` on it.
 */

import { unwrap } from './api-envelope';

/** One bar: a numerator and a denominator the API computed together. */
export interface ComplianceBar {
  completed: number;
  total: number;
}

/**
 * The payload of GET /safety/compliance.
 *
 * `equipmentChecks` IS DRAWN NOW, as a fourth bar on the Safety screen, and it
 * is not a second spelling of `inspections`: `equipment_checks` holds the quick
 * pre-use pass/flag a hand does on a piece of equipment, while
 * `equipment_inspection_submissions` holds a submitted inspection form. The two
 * share this denominator and the API reports them as separate numerators off one
 * query, so reading either as the other overstates coverage.
 *
 * IT STAYS OPTIONAL ANYWAY, and that is deliberate rather than leftover. A bar
 * this app draws in ONE place must not be able to blank the three it draws
 * everywhere — `readCompliance` returning null for a payload missing this key
 * would turn an older API deploy into a compliance panel that reads "couldn't
 * load" with three perfectly good bars in hand. The screen drops the row
 * instead, because an absent bar is not a 0/0 bar.
 */
export interface SafetyCompliance {
  meetings: ComplianceBar;
  jsas: ComplianceBar;
  inspections: ComplianceBar;
  equipmentChecks?: ComplianceBar;
}

/**
 * A bar, or null if the value cannot be read as one.
 *
 * Both halves must be real finite numbers. `Number(x)` is deliberately not used
 * — `Number(null)` and `Number('')` are both 0, which is how an absent
 * denominator becomes a zero denominator and a "no data" state becomes a "no
 * jobs" state. Negatives are rejected too: a count cannot be negative, and one
 * that is means the payload is not what we think it is.
 */
function readBar(value: unknown): ComplianceBar | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { completed, total } = value as Record<string, unknown>;
  if (typeof completed !== 'number' || !Number.isFinite(completed) || completed < 0) return null;
  if (typeof total !== 'number' || !Number.isFinite(total) || total < 0) return null;
  return { completed, total };
}

/**
 * Read a compliance response body into the three bars, or null if it cannot be.
 *
 * Pass `res.data` (the axios body), not `res`.
 *
 * Null means "ask again / say so", never "zeros". It covers every way the
 * payload can fail to arrive while the request itself reports 200:
 *
 *   - `{ success: true, data: null }` — `unwrap` returns null rather than
 *     handing back the envelope. The old `r.data.data ?? r.data ?? {}` returned
 *     the ENVELOPE here, whose `.jsas` is undefined, which then read as 0/0.
 *   - `{ success: true }` with the key dropped by JSON.stringify — same.
 *   - a string body. Railway's edge proxy answers a dead route with `index.html`
 *     and a 200 (`X-Railway-Fallback: true`), so the body is HTML. The old
 *     `??` chain fell through to that string and read `.jsas` off it as
 *     undefined — 0/0 again, from a request that never reached the container.
 *   - a payload missing a bar, or carrying a non-numeric count.
 *
 * An HTTP error does not come through here at all: the API answers 500 with
 * `{ success: false, error }`, axios rejects, and the caller sees `isError`.
 * Both paths have to land on the same error state — from the reader's side
 * "the server refused" and "the server sent something we cannot read" are one
 * fact, and neither is an empty compliance bar.
 */
export function readCompliance(body: unknown): SafetyCompliance | null {
  const payload = unwrap<unknown>(body);
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;

  const source = payload as Record<string, unknown>;
  const meetings = readBar(source.meetings);
  const jsas = readBar(source.jsas);
  const inspections = readBar(source.inspections);
  if (!meetings || !jsas || !inspections) return null;

  const equipmentChecks = readBar(source.equipmentChecks);
  return { meetings, jsas, inspections, ...(equipmentChecks ? { equipmentChecks } : {}) };
}

/**
 * The coverage percentage, or null when there is no denominator to divide by.
 *
 * Null is the answer to 0/0, and callers render it as an em dash. Do not
 * default it — see the header. `total > 0` also filters the non-finite case
 * on the way past, so a NaN denominator can never reach the division.
 */
export function compliancePct(bar: ComplianceBar): number | null {
  if (!(bar.total > 0)) return null;
  return Math.round((bar.completed / bar.total) * 100);
}
